import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Area, AreaChart, ResponsiveContainer, Tooltip, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChevronRight, X, ArrowUpRight, ArrowDownRight, Activity } from "lucide-react";
import SignalBadge from "./SignalBadge";
import { RsiGauge, MacdBar, EmaTrend } from "./IndicatorBar";
import LanguageSwitcher from "./LanguageSwitcher";
import CommandSearch from "./CommandSearch";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function loadPair(symbol, timeframe, lang) {
  const [from, to] = symbol.split("/");
  const [live, analysis] = await Promise.all([
    axios.get(`${API}/live/${from}/${to}`, { params: { timeframe } }).then(r => r.data).catch(() => null),
    axios.get(`${API}/analysis/${from}/${to}`, { params: { lang } }).then(r => r.data).catch(() => null),
  ]);
  return { symbol, live, analysis };
}

export default function Compare() {
  const [searchParams] = useSearchParams();
  const { t, lang, pn, dir } = useI18n();
  const navigate = useNavigate();
  const symbolsCsv = searchParams.get("pairs") || "";
  const symbols = symbolsCsv.split(",").filter(Boolean).slice(0, 2);
  const timeframe = searchParams.get("timeframe") || "5m";

  const [pairs, setPairs] = useState([null, null]);
  const [allPairs, setAllPairs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/live`, { params: { timeframe: "5m" } })
      .then(r => setAllPairs(r.data || []))
      .catch(() => setAllPairs([]));
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all(symbols.map(s => loadPair(s, timeframe, lang)))
      .then(res => { if (mounted) setPairs([res[0] || null, res[1] || null]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [symbolsCsv, lang, timeframe]);

  const setPairAt = (index, symbol) => {
    const next = [...symbols];
    next[index] = symbol;
    navigate(`/compare?pairs=${next.filter(Boolean).join(",")}&timeframe=${timeframe}`);
  };

  const removePair = (index) => {
    const next = [...symbols];
    next.splice(index, 1);
    if (next.length === 0) navigate("/");
    else navigate(`/compare?pairs=${next.join(",")}&timeframe=${timeframe}`);
  };

  return (
    <div className="min-h-screen grid-bg" data-testid="compare-page">
      <div className="border-b border-zinc-900 bg-[#0A0A0A]/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-white">{t("back")}</Link>
            <ChevronRight className={cn("h-4 w-4 text-zinc-700", dir === "rtl" && "rotate-180")} />
            <span className="text-white font-bold">{t("compare_title")}</span>
          </div>
          <div className="flex items-center gap-3">
            <CommandSearch pairs={allPairs} />
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[0, 1].map((i) => (
            <PairColumn
              key={i}
              index={i}
              symbol={symbols[i]}
              data={pairs[i]}
              loading={loading}
              allPairs={allPairs}
              onPickPair={(s) => setPairAt(i, s)}
              onRemove={() => removePair(i)}
              t={t}
              pn={pn}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PairColumn({ index, symbol, data, loading, allPairs, onPickPair, onRemove, t, pn }) {
  if (!symbol) {
    return (
      <Card className="bg-[#121212] border border-dashed border-zinc-800 rounded-sm p-8 min-h-[400px] flex flex-col items-center justify-center gap-4">
        <div className="text-sm text-zinc-500">{t("compare_pick")}</div>
        <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
          {allPairs.slice(0, 8).map((p) => (
            <button
              key={p.symbol}
              onClick={() => onPickPair(p.symbol)}
              data-testid={`compare-pick-${index}-${p.symbol.replace("/", "")}`}
              className="ticker text-xs font-bold px-3 py-2 rounded-sm bg-zinc-900 border border-zinc-800 text-white hover:bg-zinc-800 hover:border-zinc-700"
            >
              {p.symbol}
            </button>
          ))}
        </div>
      </Card>
    );
  }
  if (loading || !data?.live) {
    return <Skeleton className="h-[400px] bg-zinc-900 rounded-sm" />;
  }

  const { live, analysis } = data;
  const stroke = live.signal === "buy" ? "#10B981" : live.signal === "sell" ? "#EF4444" : "#F59E0B";
  const up = live.change_pct >= 0;

  return (
    <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-6 space-y-5 relative" data-testid={`compare-col-${index}`}>
      <button
        onClick={onRemove}
        data-testid={`compare-remove-${index}`}
        className="absolute top-3 end-3 p-1 rounded-sm text-zinc-500 hover:text-white hover:bg-zinc-800"
      >
        <X className="h-4 w-4" />
      </button>

      <div>
        <div className="ticker text-2xl font-bold text-white" dir="ltr">{live.symbol}</div>
        <div className="text-xs text-zinc-500">{pn(live.symbol)}</div>
      </div>

      <div className="flex items-center justify-between">
        <SignalBadge signal={live.signal} confidence={live.confidence} size="lg" />
        <div className="text-right">
          <div className="num text-2xl font-bold text-white" dir="ltr">{live.price?.toFixed(live.price > 50 ? 3 : 5)}</div>
          <div className={cn("num text-xs font-bold flex items-center gap-1 justify-end",
            up ? "text-emerald-400" : "text-red-400"
          )} dir="ltr">
            {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {up ? "+" : ""}{live.change_pct?.toFixed(3)}%
          </div>
        </div>
      </div>

      <div className="h-40" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={live.candles}>
            <defs>
              <linearGradient id={`cmp-${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.4} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
            <XAxis dataKey="date" hide />
            <YAxis domain={['auto', 'auto']} stroke="#71717a" fontSize={9} orientation="right" tick={{ fontFamily: 'JetBrains Mono' }} />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono" }}
              labelStyle={{ color: "#a1a1aa" }}
            />
            <Area type="monotone" dataKey="close" stroke={stroke} strokeWidth={2} fill={`url(#cmp-${index})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-2 pt-2 border-t border-zinc-800/60">
        <RsiGauge value={live.rsi} />
        <MacdBar hist={live.macd_hist} />
      </div>

      {analysis?.reasoning_ar && (
        <div className="pt-3 border-t border-zinc-800/60">
          <div className="flex items-center gap-2 text-emerald-400 text-[11px] mb-2">
            <Activity className="h-3 w-3" />
            <span>{t("ai_analysis")}</span>
          </div>
          <p className="text-xs leading-relaxed text-zinc-300 line-clamp-4">{analysis.reasoning_ar}</p>
        </div>
      )}
    </Card>
  );
}
